import React, { useMemo } from 'react';

import CardDialog from '~/components/CardDialog';
import LinkShare from '~/components/communications/LinkShare';
import { contactPopulate } from '~/components/Contacts/ContactUtils';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import Text from '~/components/core/TextComponents/Text';
import useFetchContact from '~/components/hooks/useFetchContact';
import { serverDateTimeToLocal } from '~/DateTimeUtils';

import { TEXTS } from './texts';
import useDocumentRequestShareTemplate from './useDocumentRequestShareTemplate';

interface DocumentRequestShareDialogProps {
  claim: { id: number; organization_id: number };
  documentRequest: { id: number; url: string; expires_at: string };
  contactId: number;
  onClose: () => void;
}

// Shares a link that was just generated the way a new video call shares its guest link: copy it, or open the
// SMS or email composer addressed to the contact and pre-filled with the organization's message for the link.
const DocumentRequestShareDialog: React.FC<DocumentRequestShareDialogProps> = ({
  claim,
  documentRequest,
  contactId,
  onClose,
}) => {
  const { data: fetchedContact, isLoading: isLoadingContact, isError: isErrorContact } = useFetchContact(contactId);
  // The organization's contact carries no primary_email, and the email composer sends to it. Memoized: the composers
  // list the contact in their hook dependencies.
  const contact = useMemo(() => (fetchedContact ? contactPopulate(fetchedContact) : undefined), [fetchedContact]);
  const { data: templateData, isError: isErrorTemplateData } = useDocumentRequestShareTemplate({
    organizationId: claim.organization_id,
    claimId: claim.id,
    documentRequestId: documentRequest.id,
    contactId,
  });

  return (
    <CardDialog
      isDialog
      title={TEXTS.SHARE_DIALOG_TITLE}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      footerActions={
        <DialogFooterActions secondaryLabel={TEXTS.CLOSE_BUTTON} onClickSecondary={onClose} showPrimary={false} />
      }
    >
      <Text variant={Text.VARIANTS.SM}>{TEXTS.SHARE_DIALOG_INTRO}</Text>
      {/* Only the SMS and email actions wait for the message: the link can be copied as soon as the contact loads. */}
      <LoadingSwitch isLoading={isLoadingContact} isError={isErrorContact}>
        {contact && (
          <LinkShare
            title={TEXTS.SHARE_LINK_TITLE}
            href={documentRequest.url}
            contact={contact}
            templateData={templateData}
            claimId={claim.id}
            keepSmsParagraphs
          />
        )}
      </LoadingSwitch>
      {isErrorTemplateData && (
        <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.ERROR}>
          {TEXTS.SHARE_TEMPLATE_FAILED}
        </Text>
      )}
      <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
        {TEXTS.GENERATED_LINK_EXPIRES(serverDateTimeToLocal(documentRequest.expires_at))}
      </Text>
    </CardDialog>
  );
};

export default DocumentRequestShareDialog;
